import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ledger, { ledgerError } from "../../../api/ledger";

/* ═══════════════════════════════════════════════════════════════════════
   COUNTER BILLING
   ───────────────────────────────────────────────────────────────────────
   Built for a keyboard and a barcode scanner, not a mouse. A cashier with a
   queue is looking at the customer, not the screen, and a till that needs a
   pointer is a till that runs at half speed.

   ── HOW THE SCANNER WORKS ──
   A USB barcode scanner is a keyboard. It types the code and presses Enter,
   faster than a person can. So there is no "scanner mode": the search box
   holds focus by default, a scan lands in it, and Enter resolves it. The one
   thing that must never happen is focus wandering somewhere a scan would be
   swallowed — hence the refocus discipline below.

   ── WHY THE SERVER OWNS THE MATHS ──
   Nothing here computes tax or totals. Every change posts to the ledger and
   the bill comes back recalculated. A till that adds up its own line and a
   server that adds up differently is a bill the customer can dispute, so
   there is exactly one place the arithmetic happens.
═══════════════════════════════════════════════════════════════════════ */

const rupees = (paise) =>
  ((Number(paise) || 0) / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* Grams display as kg once they pass a kilo, which is how a shop says it. */
const qtyLabel = (qty, unit) => {
  if (unit !== "g") return String(qty);
  return qty >= 1000 ? `${(qty / 1000).toFixed(3)} kg` : `${qty} g`;
};

const TENDERS = [
  { key: "cash", label: "Cash", hint: "F9", colour: "#0E3A2C" },
  { key: "upi", label: "UPI", hint: "F10", colour: "#5B21B6" },
  { key: "card", label: "Card", hint: "F11", colour: "#1D4ED8" },
];

export default function Pos() {
  const location = useLocation();
  const navigate = useNavigate();
  const [storeId, setStoreId] = useState(localStorage.getItem("posStoreId") || "");

  /* ── WHY A TILL NEEDS A CLOCK ──
     Two taps on + send two PATCHes. If the first one's answer arrives second,
     it repaints the bill at the quantity BEFORE the second tap: the cashier
     saw 3, the customer is charged for 2, and the only way back is to delete
     the line and scan it again. Same shape in the search box, where a slow
     answer for "ragi" lands on top of the results for "ragi flour" with a
     finger already on Enter, and the wrong product goes on the bill.
     So every answer that repaints the bill carries a ticket, and one that is
     older than an answer already painted is dropped. */
  const ticket = useRef(0);
  const painted = useRef(0);
  const nextTicket = useCallback(() => { ticket.current += 1; return ticket.current; }, []);
  const claim = useCallback((t) => {
    if (t < painted.current) return false;
    painted.current = t;
    return true;
  }, []);
  const [stores, setStores] = useState([]);
  const [bill, setBill] = useState(null);
  const [lines, setLines] = useState([]);
  const [payments, setPayments] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  /* Held bills are DRAFTS ON THE SERVER, not a list in this component. They
     were both, which meant the bill survived leaving the screen and the list of
     them did not: the draft sat in the database with nobody able to name it.
     This list is read back from the server, so it is the same list on any
     machine, after any reload, for whoever is at the counter next. */
  const [held, setHeld] = useState([]);
  const [tenderOpen, setTenderOpen] = useState(null);
  const [tenderAmount, setTenderAmount] = useState("");
  const [customer, setCustomer] = useState({ phone: "", name: "" });
  const [lastBill, setLastBill] = useState(null);

  const searchRef = useRef(null);
  const tenderRef = useRef(null);

  /* The search box is home. Anything that steals focus hands it back, because
     a scan that lands nowhere is a scan the cashier has to repeat. */
  const focusSearch = useCallback(() => {
    if (tenderOpen) return;
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [tenderOpen]);

  useEffect(() => {
    ledger
      .get("/network/godowns")
      .catch(() => null); // warms the connection; failure is not interesting here
    import("../../../api/axios").then(({ default: api }) =>
      api
        /* the back-office store picker: a bare ARRAY of
           {id, code, name, isDefault, isActive, addressLine}, default first */
        .get("/admin/stores")
        .then((r) => {
          const rows = Array.isArray(r.data) ? r.data : [];
          setStores(rows);
          if (!rows.length) return;
          /* A remembered id is only good while it still names a store. The
             ids changed at the cutover — a till that kept the old Mongo _id
             in localStorage would go on billing against a store that does
             not exist: POST /billing/bills stores store_id as plain text and
             refuses nothing, so the bills post and every search comes back
             unpriced. An unknown id is dropped for the default store. */
          const known = rows.some((s) => String(s.id) === String(storeId));
          if (!storeId || !known) {
            const first = rows.find((s) => s.isDefault) || rows[0];
            setStoreId(String(first.id));
          }
        })
        .catch(() => setStores([]))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (storeId) localStorage.setItem("posStoreId", storeId);
  }, [storeId]);

  useEffect(() => focusSearch(), [focusSearch, bill, lines.length]);

  /* ── bill lifecycle ─────────────────────────────────────────────────── */
  /* Every change already answers with the whole bill, so this only runs when
     there is nothing to go on: opening the screen, recalling a held bill. A
     scan used to be a POST and then a GET, and the second round trip is the
     one a cashier feels with a queue in front of them. */
  const apply = useCallback((data, t) => {
    if (!data?.bill) return data;
    // An answer older than one already painted describes a bill that has since
    // changed. Painting it would undo the change.
    if (t !== undefined && !claim(t)) return data;
    setBill(data.bill);
    setLines(data.lines || []);
    setPayments(data.payments || []);
    return data;
  }, [claim]);

  const refresh = useCallback(async (id) => {
    const t = nextTicket();
    const { data } = await ledger.get(`/billing/bills/${id}`);
    return apply(data, t);
  }, [apply, nextTicket]);

  // A new bill is empty by definition, so the GET that used to follow the
  // POST asked the server to describe nothing. The first scan of every
  // customer was three sequential calls before the item appeared.
  const openBill = useCallback(async () => {
    if (!storeId) { setError("choose a store first"); return null; }
    const { data } = await ledger.post("/billing/bills", {
      storeId,
      channel: "pos",
      partyPhone: customer.phone || null,
      partyName: customer.name || null,
    });
    // A bill that was just created is empty, and the server said so in the
    // answer it already gave: setting it here saves a second round trip on the
    // first scan of every customer.
    apply(data, nextTicket());
    return data.billId;
  }, [storeId, customer, apply, nextTicket]);

  const ensureBill = useCallback(async () => (bill?.id ? bill.id : await openBill()), [bill, openBill]);

  /* ── search / scan ──────────────────────────────────────────────────── */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || !storeId) { setResults([]); return undefined; }
    // Debounced: a scanner types a whole barcode in milliseconds and every
    // keystroke would otherwise be a request. The request that does go out is
    // ABORTED when the next keystroke arrives, so a slow answer for a prefix
    // can never repaint the list under a cashier who has typed past it.
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      ledger
        .get(`/billing/search`, { params: { q, storeId, limit: 12 }, signal: ctrl.signal })
        .then((r) => { setResults(r.data || []); setHighlight(0); })
        .catch((e) => { if (e.code !== "ERR_CANCELED") setResults([]); });
    }, 180);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query, storeId]);

  const addLine = useCallback(
    async (payload) => {
      setBusy(true); setError("");
      try {
        const id = await ensureBill();
        if (!id) return;
        // The line and the recalculated bill come back together.
        const t = nextTicket();
        const { data } = await ledger.post(`/billing/bills/${id}/lines`, payload);
        // Clearing the box first stops the debounced search from firing for a
        // barcode that has already been added: that request used to go out
        // ~180 ms after every single scan, and its answer flashed the results
        // list back over the shortcut pane.
        setQuery(""); setResults([]);
        apply(data, t);
      } catch (e) {
        setError(ledgerError(e));
      } finally {
        setBusy(false); focusSearch();
      }
    },
    [ensureBill, apply, focusSearch, nextTicket]
  );

  /* A scan is whatever is in the box when Enter is pressed and nothing is
     highlighted — send it as a barcode and let the server decide whether it is
     a product code or a weight label from the scale. */
  const submitSearch = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    if (results.length) {
      const item = results[highlight] || results[0];
      addLine({ productId: item.productId, sku: item.sku, qty: 1 });
    } else {
      addLine({ barcode: q, qty: 1 });
    }
  }, [query, results, highlight, addLine]);

  const removeLine = useCallback(
    async (lineId) => {
      if (!bill?.id) return;
      setBusy(true);
      const t = nextTicket();
      try {
        const { data } = await ledger.delete(`/billing/bills/${bill.id}/lines/${lineId}`);
        apply(data, t);
      } catch (e) { setError(ledgerError(e)); }
      finally { setBusy(false); focusSearch(); }
    },
    [bill, apply, focusSearch, nextTicket]
  );

  const changeQty = useCallback(
    async (line, delta) => {
      const next = Number(line.qty) + delta * (line.unit === "g" ? 50 : 1);
      if (next <= 0) return removeLine(line.id);
      /* Optimistic: the number changes under the cashier's finger and the
         server's answer replaces it a moment later. A till that waits for a
         network round trip before showing a 2 feels broken even when it is
         merely honest. */
      setLines((prev) => prev.map((l) => (l.id === line.id ? { ...l, qty: next } : l)));
      const t = nextTicket();
      try {
        const { data } = await ledger.patch(`/billing/bills/${bill.id}/lines/${line.id}`, { qty: next });
        apply(data, t);
      } catch (e) { setError(ledgerError(e)); await refresh(bill.id); }
      // No setBusy here: this call never set it, and clearing it re-enabled
      // Save & Print while a scan was still in flight.
      finally { focusSearch(); }
      return undefined;
    },
    [bill, refresh, apply, removeLine, focusSearch, nextTicket]
  );

  /* ── tender ─────────────────────────────────────────────────────────── */
  const balance = useMemo(() => {
    const total = Number(bill?.totalPaise || 0);
    const taken = payments.reduce((n, p) => n + Number(p.amountPaise || 0), 0);
    return total - taken;
  }, [bill, payments]);

  const openTender = useCallback(
    (method) => {
      if (!bill?.id || !lines.length) return;
      setTenderOpen(method);
      setTenderAmount(String((balance / 100).toFixed(2)));
      requestAnimationFrame(() => tenderRef.current?.select());
    },
    [bill, lines, balance]
  );

  const takeTender = useCallback(async () => {
    if (!tenderOpen || !bill?.id) return;
    const paise = Math.round(parseFloat(tenderAmount || "0") * 100);
    if (!paise || paise <= 0) { setTenderOpen(null); return; }
    setBusy(true); setError("");
    // Ticketed like every other repaint. A payment is the one answer that must
    // never be wiped by a slower one arriving behind it: the money is in the
    // drawer, and a bill that has forgotten it asks the customer to pay twice.
    const t = nextTicket();
    try {
      const { data } = await ledger.post(`/billing/bills/${bill.id}/tenders`, {
        method: tenderOpen, amountPaise: paise, tenderedPaise: paise,
      });
      if (data.changePaise > 0) setNotice(`Change ₹${rupees(data.changePaise)}`);
      apply(data, t);
      setTenderOpen(null); setTenderAmount("");
    } catch (e) { setError(ledgerError(e)); }
    finally { setBusy(false); focusSearch(); }
  }, [tenderOpen, tenderAmount, bill, apply, focusSearch, nextTicket]);

  /* The queued job is for the print agent and a registered thermal printer.
     When there is none — a new shop, an agent not yet installed — the job would
     sit in the queue and the cashier would watch nothing happen. So the same
     receipt is fetched as text and handed to the browser's own print dialog,
     which prints on whatever the counter machine has. */
  const printThroughBrowser = useCallback(async (billId) => {
    try {
      const { data } = await ledger.get(`/billing/bills/${billId}/receipt`, { params: { paper: "80mm" } });
      const w = window.open("", "_blank", "width=420,height=680");
      if (!w) { setNotice("Allow pop-ups to print from this screen"); return; }
      w.document.write(
        `<html><head><title>${data.docNo || "Bill"}</title><style>
           @page { margin: 4mm; }
           body { margin: 0; font-family: "Courier New", monospace; font-size: 12px; line-height: 1.35; white-space: pre; }
         </style></head><body>${(data.text || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))}</body></html>`
      );
      w.document.close();
      w.focus();
      w.print();
      // Left open on purpose: closing it immediately cancels the dialog on
      // some browsers, and a cashier can close a window.
    } catch (e) { setError(ledgerError(e)); }
  }, []);

  /* Every draft this store has open, newest first. A held bill is simply one
     that nobody is looking at. */
  const loadHeld = useCallback(async () => {
    if (!storeId) { setHeld([]); return; }
    try {
      const { data } = await ledger.get("/billing/bills", { params: { storeId, state: "draft", limit: 20 } });
      setHeld(Array.isArray(data) ? data : data?.data || []);
    } catch { /* the list is a convenience; a till with no list still bills */ }
  }, [storeId]);

  const postBill = useCallback(async () => {
    if (!bill?.id || !lines.length) return;
    if (balance > 0) { setError(`₹${rupees(balance)} still to collect`); openTender("cash"); return; }
    setBusy(true); setError("");
    try {
      const { data } = await ledger.post(`/billing/bills/${bill.id}/post`, {});
      setLastBill({ id: bill.id, docNo: data.docNo, totalPaise: data.totalPaise });
      setNotice(`${data.docNo} — ₹${rupees(data.totalPaise)}`);
      // Queue for the print agent, and never block the next customer on a
      // printer. If no printer is registered for this store the server says so,
      // and the browser prints instead of nothing printing.
      const printedId = bill.id;
      ledger
        .post(`/billing/bills/${printedId}/print`, { paper: "80mm" })
        .then((r) => { if (r.data?.queuedWithoutPrinter) printThroughBrowser(printedId); })
        .catch(() => printThroughBrowser(printedId));
      setBill(null); setLines([]); setPayments([]); setCustomer({ phone: "", name: "" });
      loadHeld();
    } catch (e) { setError(ledgerError(e)); }
    finally { setBusy(false); focusSearch(); }
  }, [bill, lines, balance, openTender, focusSearch, printThroughBrowser, loadHeld]);


  useEffect(() => { loadHeld(); }, [loadHeld]);

  /* Throwing a bill away. Holding keeps it for later; this is for the ones
     there is no later for -- opened by accident, or the customer changed their
     mind and walked off. Without it every such bill stayed in Open bills for
     ever, because a draft cannot be posted and, until today, could not be
     cancelled either. The confirm is deliberate: the lines are gone after it. */
  const discardBill = useCallback(
    async (id, label) => {
      if (!id) return;
      if (!window.confirm(`Discard ${label || "this bill"}? The items on it are not saved.`)) return;
      try {
        await ledger.post(`/billing/bills/${id}/cancel`, { reason: "discarded at the till" });
        if (bill?.id === id) { setBill(null); setLines([]); setPayments([]); setCustomer({ phone: "", name: "" }); }
        setNotice("Bill discarded");
        loadHeld();
      } catch (e) { setError(ledgerError(e)); }
      finally { focusSearch(); }
    },
    [bill, loadHeld, focusSearch]
  );

  /* Arriving from the dashboard with a bill in hand: /admin/pos?bill=12&store=…
     The parameter is spent as soon as it is used, so a reload does not drag
     the cashier back to a bill they have since finished.

     The store travels with the bill because everything else on this screen is
     scoped to it: search prices against a store, the shelf figures are that
     store's, and a new line takes the store's price. Opening one branch's
     draft while the till is set to another looks entirely normal and is wrong
     in every number. */
  const arrivedWith = useRef(false);
  useEffect(() => {
    if (arrivedWith.current) return;
    const params = new URLSearchParams(location.search);
    const id = params.get("bill");
    if (!id) return;
    const store = params.get("store");
    arrivedWith.current = true;
    navigate("/admin/pos", { replace: true });
    if (store) setStoreId(store);
    refresh(id)
      .then((d) => { const s = d?.bill?.storeId; if (s) setStoreId(String(s)); })
      .catch(() => setError("That bill is no longer open"));
  }, [location.search, navigate, refresh]);

  const holdBill = useCallback(() => {
    if (!bill?.id || !lines.length) return;
    // Nothing is sent: the draft is already on the server. Letting go of it
    // here IS holding it.
    setBill(null); setLines([]); setPayments([]);
    setNotice("Bill held — it is under Open bills");
    loadHeld();
    focusSearch();
  }, [bill, lines, loadHeld, focusSearch]);

  const recallBill = useCallback(
    async (id) => {
      await refresh(id);
      loadHeld();
      focusSearch();
    },
    [refresh, loadHeld, focusSearch]
  );

  /* ── keyboard ───────────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      // Let the tender dialog own the keyboard while it is open.
      if (tenderOpen) {
        if (e.key === "Escape") { e.preventDefault(); setTenderOpen(null); focusSearch(); }
        if (e.key === "Enter") { e.preventDefault(); takeTender(); }
        return;
      }
      // Alt+letter first: it is the shortcut that always arrives. The browser
      // reserves no Alt combination the page can see, and a keyboard in
      // media mode still sends the letter.
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        const letter = (e.key || "").toLowerCase();
        const byLetter = {
          s: focusSearch,
          h: holdBill,
          r: () => { if (held.length) recallBill(held[held.length - 1].id); },
          c: () => openTender("cash"),
          u: () => openTender("upi"),
          d: () => openTender("card"),
          p: postBill,
        }[letter];
        if (byLetter) { e.preventDefault(); byLetter(); return; }
      }
      // Ctrl+Enter posts: the one every cashier reaches for when a hand is
      // already on the keyboard, and the replacement for F12, which Chrome
      // keeps for its devtools and will not hand over.
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); postBill(); return; }

      // e.code as well as e.key, so a keyboard that reports the key oddly
      // still matches on its physical position.
      switch (e.key === "Unidentified" ? e.code : e.key) {
        case "F1": e.preventDefault(); focusSearch(); break;
        case "F6": e.preventDefault(); holdBill(); break;
        case "F7": e.preventDefault(); if (held.length) recallBill(held[held.length - 1].id); break;
        case "F9": e.preventDefault(); openTender("cash"); break;
        case "F10": e.preventDefault(); openTender("upi"); break;
        case "F11": e.preventDefault(); openTender("card"); break;
        case "F12": e.preventDefault(); postBill(); break;
        case "Escape":
          e.preventDefault();
          if (query) { setQuery(""); setResults([]); } else { setError(""); setNotice(""); }
          focusSearch();
          break;
        case "ArrowDown":
          if (results.length) { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
          break;
        case "ArrowUp":
          if (results.length) { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
          break;
        default: break;
      }
    };
    // Capture, so a focused input or a stray handler cannot swallow a till
    // shortcut before it arrives.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [tenderOpen, takeTender, focusSearch, holdBill, held, recallBill, openTender, postBill, query, results]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  /* ── render ─────────────────────────────────────────────────────────── */
  return (
    <div style={S.page}>
      <div style={S.left}>
        <div style={S.searchRow}>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitSearch(); } }}
            placeholder="Scan a barcode, or type to search…            F1"
            style={S.search}
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)} style={S.store}>
            <option value="">Store…</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {results.length > 0 && (
          <div style={S.results}>
            {results.map((r, i) => (
              <button
                key={`${r.productId}:${r.sku}`}
                onClick={() => addLine({ productId: r.productId, sku: r.sku, qty: 1 })}
                style={{ ...S.result, ...(i === highlight ? S.resultOn : null) }}
              >
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={S.resultName}>{r.name}</div>
                  <div style={S.resultMeta}>
                    {r.sku} · {r.soldBy === "weight" ? "per kg" : "per pack"}
                    {r.available != null && (
                      <span style={{ color: r.available > 0 ? "#166534" : "#b91c1c" }}>
                        {"  ·  "}{r.available > 0 ? `${r.available} in stock` : "out of stock"}
                      </span>
                    )}
                  </div>
                </div>
                <div style={S.resultPrice}>{r.pricePaise != null ? `₹${rupees(r.pricePaise)}` : "no price"}</div>
              </button>
            ))}
          </div>
        )}

        {results.length === 0 && (
          <div style={S.hintPane}>
            <div style={S.hintTitle}>Keyboard</div>
            <div style={{ fontSize: 10.5, color: "#9fb4ab", marginBottom: 8, lineHeight: 1.45 }}>
              F9 / F10 / F11 / F12 do the same on a till whose keyboard sends
              them. Alt and Ctrl always arrive.
            </div>
            <div style={S.hintGrid}>
              {[
                ["Alt+C", "Cash"], ["Alt+U", "UPI"], ["Alt+D", "Card"],
                ["Ctrl+↵", "Save & print"], ["Alt+H", "Hold bill"], ["Alt+R", "Recall"],
                ["Alt+S", "Search"], ["Esc", "Clear"], ["↑ ↓", "Pick result"],
              ].map(([k, v]) => (
                <div key={k} style={S.hintRow}><kbd style={S.kbd}>{k}</kbd><span>{v}</span></div>
              ))}
            </div>
            {held.length > 0 && (
              <>
                <div style={{ ...S.hintTitle, marginTop: 18 }}>Open bills ({held.length})</div>
                {held.map((h) => (
                  <div key={h.id} style={S.heldRow}>
                    <button onClick={() => recallBill(h.id)} style={S.heldRecall}>
                      #{h.docNo || h.id} · {h.lineCount} item(s) · ₹{rupees(h.totalPaise)}
                    </button>
                    <button
                      onClick={() => discardBill(h.id, `bill #${h.id}`)}
                      style={S.heldDiscard}
                      title="Discard this bill"
                      aria-label={`Discard bill ${h.id}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </>
            )}
            {lastBill && (
              <div style={S.lastBill}>
                Last: <strong>{lastBill.docNo}</strong> · ₹{rupees(lastBill.totalPaise)}
                <button
                  style={S.reprint}
                  onClick={() => ledger.post(`/billing/bills/${lastBill.id}/print`, { paper: "80mm" })
                    .then((r) => {
                      if (r.data?.queuedWithoutPrinter) printThroughBrowser(lastBill.id);
                      else setNotice("Sent to the printer");
                    })
                    .catch((e) => setError(ledgerError(e)))}
                >
                  Reprint
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={S.right}>
        <div style={S.billHead}>
          <span>{bill?.id ? `Bill #${bill.id}` : "New bill"}</span>
          <span style={S.billCount}>{lines.length} item{lines.length === 1 ? "" : "s"}</span>
        </div>

        <div style={S.lines}>
          {lines.length === 0 && <div style={S.empty}>Scan or search to begin</div>}
          {lines.map((l) => (
            <div key={l.id} style={S.line}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.lineName}>{l.description}</div>
                <div style={S.lineMeta}>
                  {qtyLabel(l.qty, l.unit)} × ₹{rupees(l.ratePaise)}
                  {l.unit === "g" ? "/kg" : ""}
                  {l.rateBp > 0 && <span style={S.gst}>GST {l.rateBp / 100}%</span>}
                  {l.scannedBarcode && <span style={S.scanned}>scanned</span>}
                </div>
              </div>
              <div style={S.stepper}>
                <button style={S.step} onClick={() => changeQty(l, -1)} tabIndex={-1}>−</button>
                <button style={S.step} onClick={() => changeQty(l, +1)} tabIndex={-1}>+</button>
              </div>
              <div style={S.lineTotal}>₹{rupees(l.totalPaise)}</div>
              <button style={S.del} onClick={() => removeLine(l.id)} tabIndex={-1} title="Remove">×</button>
            </div>
          ))}
        </div>

        <div style={S.totals}>
          <Row label="Taxable" value={bill?.taxablePaise} />
          {Number(bill?.discountPaise) > 0 && <Row label="Discount" value={bill?.discountPaise} minus />}
          {Number(bill?.cgstPaise) > 0 && <Row label="CGST" value={bill?.cgstPaise} />}
          {Number(bill?.sgstPaise) > 0 && <Row label="SGST" value={bill?.sgstPaise} />}
          {Number(bill?.igstPaise) > 0 && <Row label="IGST" value={bill?.igstPaise} />}
          {Number(bill?.roundOffPaise) !== 0 && <Row label="Round off" value={bill?.roundOffPaise} />}
          <div style={S.grand}>
            <span>Total</span>
            <span>₹{rupees(bill?.totalPaise)}</span>
          </div>
          {payments.length > 0 && (
            <div style={S.paid}>
              {payments.map((p) => (
                <div key={p.id} style={S.paidRow}>
                  <span>{p.method.toUpperCase()}{p.reference ? ` · ${p.reference}` : ""}</span>
                  <span>₹{rupees(p.amountPaise)}</span>
                </div>
              ))}
              <div style={{ ...S.paidRow, fontWeight: 800, color: balance > 0 ? "#b91c1c" : "#166534" }}>
                <span>{balance > 0 ? "Balance" : "Settled"}</span>
                <span>₹{rupees(Math.abs(balance))}</span>
              </div>
            </div>
          )}
        </div>

        {(error || notice) && (
          <div style={error ? S.error : S.notice}>{error || notice}</div>
        )}

        <div style={S.tenderRow}>
          {TENDERS.map((t) => (
            <button
              key={t.key}
              onClick={() => openTender(t.key)}
              disabled={!lines.length || busy}
              style={{ ...S.tender, background: t.colour, opacity: !lines.length || busy ? 0.45 : 1 }}
            >
              {t.label}<span style={S.tenderHint}>{t.hint}</span>
            </button>
          ))}
        </div>
        <button
          onClick={postBill}
          disabled={!lines.length || busy}
          style={{ ...S.save, opacity: !lines.length || busy ? 0.45 : 1 }}
        >
          {busy ? "Working…" : "Save & Print"}<span style={S.tenderHint}>F12</span>
        </button>
        <button onClick={holdBill} disabled={!lines.length} style={S.hold}>Hold (F6)</button>
        {/* Hold keeps a bill for later. This is for the ones there is no later
            for, and it is why an abandoned draft no longer sits in the list
            for ever. */}
        <button
          onClick={() => discardBill(bill?.id, `bill #${bill?.id}`)}
          disabled={!bill?.id}
          style={{ ...S.discard, opacity: bill?.id ? 1 : 0.45 }}
        >
          Discard
        </button>
      </div>

      {tenderOpen && (
        <div style={S.modalWrap} onClick={() => { setTenderOpen(null); focusSearch(); }}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalTitle}>{tenderOpen.toUpperCase()}</div>
            <div style={S.modalBalance}>Balance ₹{rupees(balance)}</div>
            <input
              ref={tenderRef}
              value={tenderAmount}
              onChange={(e) => setTenderAmount(e.target.value)}
              style={S.modalInput}
              inputMode="decimal"
              autoFocus
            />
            {tenderOpen === "cash" && (
              <div style={S.quickCash}>
                {[50, 100, 200, 500, 2000].map((n) => (
                  <button key={n} style={S.quick} onClick={() => setTenderAmount(String(n))}>₹{n}</button>
                ))}
                <button style={S.quick} onClick={() => setTenderAmount((balance / 100).toFixed(2))}>Exact</button>
              </div>
            )}
            <div style={S.modalActions}>
              <button style={S.modalCancel} onClick={() => { setTenderOpen(null); focusSearch(); }}>Esc</button>
              <button style={S.modalOk} onClick={takeTender}>Take ⏎</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, minus }) {
  return (
    <div style={S.row}>
      <span>{label}</span>
      <span>{minus ? "−" : ""}₹{rupees(value)}</span>
    </div>
  );
}

/* Inline styles keep this screen self-contained: it is the one page that must
   not break because a shared stylesheet changed. */
const S = {
  page: { display: "grid", gridTemplateColumns: "minmax(0,1fr) 420px", gap: 14, height: "calc(100vh - 90px)", fontFamily: "Inter, system-ui, sans-serif" },
  left: { display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
  searchRow: { display: "flex", gap: 10 },
  search: { flex: 1, height: 54, fontSize: 17, padding: "0 16px", borderRadius: 12, border: "2px solid #C9952A", outline: "none", background: "#fff" },
  store: { height: 54, fontSize: 14, padding: "0 12px", borderRadius: 12, border: "1px solid #E8DFC8", background: "#fff", minWidth: 150 },
  results: { flex: 1, overflowY: "auto", background: "#fff", borderRadius: 14, border: "1px solid #E8DFC8" },
  result: { display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 16px", border: "none", borderBottom: "1px solid #F1E6C9", background: "transparent", cursor: "pointer" },
  resultOn: { background: "#FDF6E3", boxShadow: "inset 3px 0 0 #C9952A" },
  resultName: { fontSize: 14, fontWeight: 600, color: "#15281D" },
  resultMeta: { fontSize: 12, color: "#6E7A68", marginTop: 2 },
  resultPrice: { fontSize: 15, fontWeight: 700, color: "#06923E", whiteSpace: "nowrap" },
  hintPane: { flex: 1, background: "#fff", borderRadius: 14, border: "1px solid #E8DFC8", padding: 20, overflowY: "auto" },
  hintTitle: { fontSize: 11, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: "#C9952A", marginBottom: 10 },
  hintGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 },
  hintRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#4C5C46" },
  kbd: { display: "inline-block", minWidth: 34, textAlign: "center", padding: "3px 6px", borderRadius: 6, border: "1px solid #E8DFC8", background: "#FDF6E3", fontFamily: "ui-monospace, monospace", fontSize: 11, fontWeight: 700, color: "#0E3A2C" },
  heldRow: { display: "flex", alignItems: "stretch", width: "100%", marginBottom: 6, borderRadius: 10, border: "1px solid #E8DFC8", background: "#FDF6E3", overflow: "hidden" },
  heldRecall: { flex: 1, textAlign: "left", padding: "10px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, fontFamily: "inherit", color: "inherit" },
  heldDiscard: { width: 36, border: "none", borderLeft: "1px solid #E8DFC8", background: "transparent", cursor: "pointer", fontSize: 13, color: "#B45309", fontFamily: "inherit" },
  lastBill: { marginTop: 18, padding: 12, borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0", fontSize: 13, display: "flex", alignItems: "center", gap: 10 },
  reprint: { marginLeft: "auto", padding: "6px 12px", borderRadius: 8, border: "1px solid #0E3A2C", background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 },

  right: { display: "flex", flexDirection: "column", gap: 10, background: "#fff", borderRadius: 14, border: "1px solid #E8DFC8", padding: 14, minHeight: 0 },
  billHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 15, fontWeight: 800, color: "#0E3A2C" },
  billCount: { fontSize: 12, fontWeight: 600, color: "#6E7A68" },
  lines: { flex: 1, overflowY: "auto", minHeight: 120 },
  empty: { padding: "40px 10px", textAlign: "center", color: "#9AA693", fontSize: 13 },
  line: { display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: "1px dashed #E8DFC8" },
  lineName: { fontSize: 13, fontWeight: 600, color: "#15281D", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  lineMeta: { fontSize: 11, color: "#6E7A68", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" },
  gst: { color: "#B4581A" },
  scanned: { color: "#0E3A2C", fontStyle: "italic" },
  stepper: { display: "flex", gap: 4 },
  step: { width: 26, height: 26, borderRadius: 6, border: "1px solid #E8DFC8", background: "#FDF6E3", cursor: "pointer", fontSize: 15, lineHeight: 1 },
  lineTotal: { minWidth: 78, textAlign: "right", fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  del: { width: 24, height: 24, borderRadius: 6, border: "none", background: "transparent", color: "#b91c1c", cursor: "pointer", fontSize: 18, lineHeight: 1 },

  totals: { borderTop: "2px solid #E8DFC8", paddingTop: 10 },
  row: { display: "flex", justifyContent: "space-between", fontSize: 13, color: "#4C5C46", padding: "3px 0", fontVariantNumeric: "tabular-nums" },
  grand: { display: "flex", justifyContent: "space-between", fontSize: 22, fontWeight: 800, color: "#0E3A2C", marginTop: 8, paddingTop: 8, borderTop: "1px solid #E8DFC8", fontVariantNumeric: "tabular-nums" },
  paid: { marginTop: 10, paddingTop: 8, borderTop: "1px dashed #E8DFC8" },
  paidRow: { display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0", fontVariantNumeric: "tabular-nums" },

  error: { padding: "9px 12px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12.5 },
  notice: { padding: "9px 12px", borderRadius: 8, background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534", fontSize: 12.5 },

  tenderRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 },
  tender: { height: 46, borderRadius: 10, border: "none", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 },
  tenderHint: { fontSize: 10, fontWeight: 600, opacity: 0.75 },
  save: { height: 54, borderRadius: 12, border: "none", background: "#C9952A", color: "#15281D", fontSize: 17, fontWeight: 800, cursor: "pointer", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 },
  hold: { height: 36, borderRadius: 10, border: "1px solid #E8DFC8", background: "#FDF6E3", fontSize: 12.5, fontWeight: 600, cursor: "pointer" },
  discard: { height: 36, borderRadius: 10, border: "1px solid #E8DFC8", background: "transparent", color: "#B45309", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },

  modalWrap: { position: "fixed", inset: 0, background: "rgba(21,40,29,.45)", display: "grid", placeItems: "center", zIndex: 90 },
  modal: { width: 380, background: "#fff", borderRadius: 16, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,.25)" },
  modalTitle: { fontSize: 12, fontWeight: 800, letterSpacing: ".12em", color: "#C9952A" },
  modalBalance: { fontSize: 14, color: "#4C5C46", marginTop: 4 },
  modalInput: { width: "100%", height: 62, fontSize: 30, fontWeight: 800, textAlign: "right", padding: "0 14px", marginTop: 12, borderRadius: 12, border: "2px solid #0E3A2C", outline: "none", fontVariantNumeric: "tabular-nums" },
  quickCash: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 },
  quick: { padding: "7px 12px", borderRadius: 8, border: "1px solid #E8DFC8", background: "#FDF6E3", cursor: "pointer", fontSize: 12.5, fontWeight: 700 },
  modalActions: { display: "flex", gap: 10, marginTop: 16 },
  modalCancel: { flex: 1, height: 44, borderRadius: 10, border: "1px solid #E8DFC8", background: "#fff", cursor: "pointer", fontWeight: 700 },
  modalOk: { flex: 2, height: 44, borderRadius: 10, border: "none", background: "#0E3A2C", color: "#fff", cursor: "pointer", fontWeight: 800 },
};
